import {Button, IconButton} from '@wandb/components';
import React, {type ChangeEvent, type FC, useMemo, useState} from 'react';

import {TextArea} from '../Form/TextArea';
import {TextField} from '../Form/TextField';
import {SensitiveInfoWrapper} from '../SensitiveInfoWrapper';
import {Tailwind} from '../Tailwind';
import {
  DUPLICATE_SECRET_NAME_WARNING,
  hasDuplicateSecretName,
  SECRET_NAME_ERROR,
  validateSecretName,
} from './validation';

export type SecretFormMode = 'create' | 'edit';

export type SecretFormState = {
  secretName: string;
  setSecretName: (name: string) => void;
  secretValue: string;
  setSecretValue: (value: string) => void;
  isSecretValueVisible: boolean;
  setIsSecretValueVisible: React.Dispatch<React.SetStateAction<boolean>>;
  showNameError: boolean;
  isDuplicate: boolean;
  isSaving: boolean;
  canSave: boolean;
  handleSave: () => Promise<void>;
};

export function useSecretFormState({
  mode,
  initialSecretName,
  existingSecretNames,
  isSaving,
  onSave,
}: {
  mode: SecretFormMode;
  initialSecretName: string;
  existingSecretNames: string[];
  isSaving: boolean;
  onSave: (input: {secretName: string; secretValue: string}) => Promise<void>;
}): SecretFormState {
  const isEditing = mode === 'edit';
  const [secretName, setSecretName] = useState(initialSecretName);
  const [secretValue, setSecretValue] = useState('');
  const [isSecretValueVisible, setIsSecretValueVisible] = useState(false);

  const isNameValid = validateSecretName(secretName);
  const isDuplicate =
    !isEditing && hasDuplicateSecretName(secretName, existingSecretNames);
  const showNameError = !isEditing && secretName !== '' && !isNameValid;
  const canSave =
    secretName !== '' &&
    isNameValid &&
    !isDuplicate &&
    secretValue !== '' &&
    !isSaving;

  const handleSave = async () => {
    if (!canSave) {
      return;
    }
    await onSave({secretName, secretValue});
  };

  return {
    secretName,
    setSecretName,
    secretValue,
    setSecretValue,
    isSecretValueVisible,
    setIsSecretValueVisible,
    showNameError,
    isDuplicate,
    isSaving,
    canSave,
    handleSave,
  };
}

type SecretFormFieldsProps = {
  mode: SecretFormMode;
  state: SecretFormState;
  banner?: React.ReactNode;
  renderNameWarning?: (secretName: string) => React.ReactNode;
};

export const SecretFormFields: FC<SecretFormFieldsProps> = ({
  mode,
  state,
  banner,
  renderNameWarning,
}) => {
  const isEditing = mode === 'edit';
  const {isSaving} = state;
  const valueLabel = isEditing ? 'New value' : 'Value';
  const valuePlaceholder = isEditing
    ? 'Paste the new secret value'
    : 'Paste your secret value';
  const visibilityToggleLabel = state.isSecretValueVisible
    ? 'Hide secret value'
    : 'Reveal secret value';
  const valueTextAreaStyle = useMemo(
    () => ({
      height: '80px',
      minHeight: '80px',
      // @ts-ignore: WebkitTextSecurity is a non-standard CSS property
      WebkitTextSecurity: state.isSecretValueVisible ? 'none' : 'disc',
    }),
    [state.isSecretValueVisible]
  );

  return (
    <Tailwind>
      <div className="flex flex-col gap-16 px-16 py-16">
        {banner}

        <div>
          <label
            htmlFor="secret-form-name"
            className="block mb-4 text-base font-semibold text-moon-800">
            Name
          </label>
          <SensitiveInfoWrapper>
            {isEditing ? (
              <div className="rounded-[5px] [&_input:disabled]:!bg-moon-200 [&_input:disabled]:!text-moon-500">
                <TextField
                  id="secret-form-name"
                  ariaLabel="Name"
                  value={state.secretName}
                  disabled
                />
              </div>
            ) : (
              <div className="rounded-[5px]">
                <TextField
                  id="secret-form-name"
                  ariaLabel="Name"
                  placeholder="Ex. OPENAI_API_KEY"
                  value={state.secretName}
                  onChange={state.setSecretName}
                  disabled={isSaving}
                  errorState={state.showNameError}
                  maxLength={255}
                  autoComplete="off"
                />
              </div>
            )}
          </SensitiveInfoWrapper>
          {state.showNameError && (
            <p className="mt-4 text-sm leading-5 text-red-550">
              {SECRET_NAME_ERROR}
            </p>
          )}
          {state.isDuplicate && (
            <p className="mt-4 text-sm leading-5 text-gold-650">
              {DUPLICATE_SECRET_NAME_WARNING}
            </p>
          )}
        </div>

        {renderNameWarning?.(state.secretName)}

        <SensitiveInfoWrapper>
          <div>
            <label
              htmlFor="secret-form-value"
              className="block mb-4 text-base font-semibold text-moon-800">
              {valueLabel}
            </label>
            {isEditing && (
              <p className="mb-8 text-sm leading-5 text-moon-500">
                This will replace the current value immediately. Any feature
                using this secret will use the new value on its next call.
              </p>
            )}
            <div className="relative rounded-[5px]">
              <TextArea
                id="secret-form-value"
                aria-label={valueLabel}
                placeholder={valuePlaceholder}
                value={state.secretValue}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                  state.setSecretValue(event.target.value)
                }
                disabled={isSaving}
                className="resize-none rounded-[5px] pr-56"
                style={valueTextAreaStyle}
                autoComplete="new-password"
              />
              <div className="absolute right-12 top-1/2 -translate-y-1/2">
                <IconButton
                  icon={
                    state.isSecretValueVisible
                      ? 'visibility-checked'
                      : 'visibility-unchecked'
                  }
                  aria-label={visibilityToggleLabel}
                  tooltip={{children: visibilityToggleLabel}}
                  variant="ghost"
                  isDisabled={isSaving}
                  onPress={() =>
                    state.setIsSecretValueVisible(visible => !visible)
                  }
                />
              </div>
            </div>
          </div>
        </SensitiveInfoWrapper>
      </div>
    </Tailwind>
  );
};

type SecretFormFooterProps = {
  mode: SecretFormMode;
  state: SecretFormState;
  saveLabel?: string;
  onCancel: () => void;
};

/** Cancel/save buttons for the secret form. Must be rendered inside a `Tailwind` scope. */
export const SecretFormFooter: FC<SecretFormFooterProps> = ({
  mode,
  state,
  saveLabel,
  onCancel,
}) => (
  <div className="flex w-full gap-16 border-t border-moon-250 px-16 py-16">
    <div className="min-w-0 flex-1">
      <Button
        fullWidth
        variant="secondary"
        size="md"
        isDisabled={state.isSaving}
        onPress={onCancel}>
        Cancel
      </Button>
    </div>
    <div className="min-w-0 flex-1">
      <Button
        fullWidth
        variant="primary"
        size="md"
        isDisabled={!state.canSave}
        isPending={state.isSaving}
        isPendingAriaLabel="Saving secret"
        onPress={state.handleSave}>
        {saveLabel ?? (mode === 'edit' ? 'Replace secret' : 'Save secret')}
      </Button>
    </div>
  </div>
);

const fullHeightStyle = {height: '100%'} as const;

type CreateSecretFormContentProps = {
  existingSecretNames: string[];
  isSaving?: boolean;
  onSave: (input: {secretName: string; secretValue: string}) => Promise<void>;
  onCancel: () => void;
  banner?: React.ReactNode;
  renderNameWarning?: (secretName: string) => React.ReactNode;
  saveLabel?: string;
};

/**
 * Full-height secret form (fields plus footer) for embedding inside a drawer
 * subview that provides its own chrome. Create-only: editing an existing
 * secret is supported via `SecretDrawer`, which seeds the initial name.
 */
export const CreateSecretFormContent: FC<CreateSecretFormContentProps> = ({
  existingSecretNames,
  isSaving = false,
  onSave,
  onCancel,
  banner,
  renderNameWarning,
  saveLabel,
}) => {
  const state = useSecretFormState({
    mode: 'create',
    initialSecretName: '',
    existingSecretNames,
    isSaving,
    onSave,
  });

  // Utility classes are compiled to `.tw-style ...` descendant selectors, so
  // they don't apply to the `Tailwind` element itself. Use an inline style for
  // the outer height and put the flex layout on an inner div so this works on
  // surfaces without a `.tw-style` ancestor (e.g. the Launch job drawer).
  return (
    <Tailwind style={fullHeightStyle}>
      <div className="flex h-full flex-col">
        <div className="min-h-0 flex-1 overflow-auto">
          <SecretFormFields
            mode="create"
            state={state}
            banner={banner}
            renderNameWarning={renderNameWarning}
          />
        </div>
        <SecretFormFooter
          mode="create"
          state={state}
          saveLabel={saveLabel}
          onCancel={onCancel}
        />
      </div>
    </Tailwind>
  );
};
